// The DFT module's results and runs (G-4): the density of states of a run, the project's runs (show one, start a new
// recipe from one, download its provenance record, compare two or three), the comparison (the bands overlaid with the
// Fermi levels at zero, and the settings side by side), and the Quantum ESPRESSO hours left this month.
import { Alert, Badge, Button, Card, Checkbox, Group, NumberInput, ScrollArea, SegmentedControl, Stack, Switch, Table, Text,
  Tooltip } from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ErrorBox, notifyError } from "../../components/Errors";
import { PALETTE, Plot } from "../../components/Plot";
import { download, formatDate, get, type Job } from "../../lib/api";
import { dosCurves, fmtHours, qeCompare, qeDos, qeRuns, type QeBudget, type QeCompare, type QeDos, type QeRun,
  type Recipe } from "../../lib/qe";

const GRID = "#e9e9e6";

/** The DOS as a Plotly figure: x = E - E_F, the total in black, the parts in colour (spin down below zero). */
export function dosFigure(d: QeDos, by: "element" | "orbital" | "defect", emin: number, emax: number) {
  const ef = d.fermi_eV ?? 0;
  const x = d.energies.map((e) => e - ef);
  const curves = dosCurves(d, by);
  const names = [...new Set(curves.map((c) => c.name.replace(/ [↑↓]$/, "")))];
  const data = curves.map((c) => {
    const base = c.name.replace(/ [↑↓]$/, "");
    const k = names.indexOf(base);
    const color = base === "Total" ? "#222222" : PALETTE[(k - 1) % PALETTE.length];
    return { x, y: c.y, type: "scatter", mode: "lines", name: base, legendgroup: base, showlegend: c.spin !== "down",
      line: { color, width: base === "Total" ? 1.6 : 1.2 },
      fill: base === "Total" ? undefined : "tozeroy", opacity: 0.9,
      hovertemplate: `${c.name}<br>E − E<sub>F</sub> = %{x:.2f} eV<br>%{y:.3f} states/eV<extra></extra>` };
  });
  const layout = {
    xaxis: { title: { text: "E − E<sub>F</sub> (eV)" }, range: [emin, emax], gridcolor: GRID, zeroline: false },
    yaxis: { title: { text: d.spin ? "DOS (states/eV/cell), ↑ up  ↓ down" : "DOS (states/eV/cell)" }, gridcolor: GRID,
      zeroline: d.spin, rangemode: d.spin ? "normal" : "tozero" },
    shapes: [{ type: "line", xref: "x", yref: "paper", x0: 0, x1: 0, y0: 0, y1: 1, line: { color: "#898781", dash: "dash", width: 1 } }],
    legend: { orientation: "h", x: 1, xanchor: "right", y: 1.1 }, margin: { l: 60, r: 20, t: 40, b: 50 },
  };
  return { data, layout };
}

export function DosView({ jobId }: { jobId: string }) {
  const q = useQuery({ queryKey: ["qe-dos", jobId], queryFn: () => qeDos(jobId), staleTime: Infinity });
  const [by, setBy] = useState<"element" | "orbital" | "defect">("element");
  const [range, setRange] = useState({ emin: -8, emax: 6 });
  const fig = useMemo(() => (q.data ? dosFigure(q.data, by, range.emin, range.emax) : null), [q.data, by, range]);
  const d = q.data;
  return (
    <Stack gap="xs" data-testid="dos-view">
      <ErrorBox error={q.error} />
      {d && <Text size="sm">Density of states · {d.kmesh ? `${d.kmesh.join(" × ")} k-mesh` : "uniform k-mesh"}
        {d.degauss_Ry ? ` · broadening ${d.degauss_Ry} Ry (${(d.degauss_Ry * 13.6057).toFixed(2)} eV)` : ""}
        {d.fermi_eV != null ? ` · E_F = ${d.fermi_eV.toFixed(3)} eV (SCF)` : ""}{d.spin ? " · spin-polarised" : ""}</Text>}
      <Group gap="xs" align="flex-end">
        <SegmentedControl size="xs" value={by} onChange={(v) => setBy(v as "element" | "orbital" | "defect")}
          data={[{ value: "element", label: "Per element" }, { value: "orbital", label: "Per element and orbital" },
            ...(d?.groups?.length ? [{ value: "defect", label: "On the defect and its shells" }] : [])]} />
        <NumberInput size="xs" w={90} label="E min (eV)" value={range.emin} step={1} max={0}
          onChange={(v) => typeof v === "number" && setRange((r) => ({ ...r, emin: v }))} />
        <NumberInput size="xs" w={90} label="E max (eV)" value={range.emax} step={1} min={0}
          onChange={(v) => typeof v === "number" && setRange((r) => ({ ...r, emax: v }))} />
      </Group>
      {fig && <Plot data={fig.data} layout={fig.layout} height={420} />}
    </Stack>
  );
}

/** "Quantum ESPRESSO time this month: 2 h 10 min of 20 h used, 17 h 50 min left". */
export function BudgetLine({ budget }: { budget?: QeBudget }) {
  if (!budget) return null;
  if (budget.hours <= 0) return <Text size="xs" c="dimmed" data-testid="qe-budget">Your plan has no Quantum ESPRESSO hours.</Text>;
  const out = budget.left <= 0;
  return (
    <Text size="xs" c={out ? "red" : "dimmed"} data-testid="qe-budget">
      Quantum ESPRESSO time this month: {fmtHours(budget.used)} of {budget.hours} h used
      {out ? " - none left until the 1st (UTC); an administrator can give you more." : `, ${fmtHours(budget.left)} left.`}
    </Text>
  );
}

const STATUS_COLOUR: Record<string, string> = { succeeded: "teal", failed: "red", cancelled: "gray", running: "blue", queued: "yellow" };
const fmtSecs = (s: number | null) => (s == null ? "" : s < 90 ? `${Math.round(s)} s` : s < 5400 ? `${(s / 60).toFixed(1)} min` : `${(s / 3600).toFixed(1)} h`);

/** The project's runs: show one, start a new recipe from one, download its provenance, compare 2-3. */
export function RunHistory({ projectId, current, onShow, onDuplicate, refreshKey }: {
  projectId: string; current?: string; onShow: (jobId: string) => void;
  onDuplicate: (recipe: Recipe, template: string | null) => void; refreshKey?: unknown;
}) {
  const runs = useQuery({ queryKey: ["qe-runs", projectId, refreshKey], queryFn: () => qeRuns(projectId) });
  const [picked, setPicked] = useState<string[]>([]);
  const [comparing, setComparing] = useState<string[] | null>(null);
  const dup = useMutation({
    mutationFn: (id: string) => get<Job>(`/jobs/${id}`),
    onSuccess: (j) => {
      const p = j.params as { recipe?: Recipe; template?: string };
      if (p.recipe) onDuplicate(p.recipe, p.template ?? null);
    },
    onError: (e) => notifyError(e),
  });
  const getProv = useMutation({ mutationFn: (r: QeRun) => download(`/files/${r.provenance_file}/download`, `${r.seed ?? "run"}_provenance.json`),
    onError: (e) => notifyError(e) });
  const list = runs.data ?? [];
  if (!runs.isLoading && list.length === 0) return null;
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < 3 ? [...p, id] : p));
  return (
    <Card withBorder data-testid="run-history">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600}>Runs of this project</Text>
          <Group gap="xs">
            <Text size="xs" c="dimmed">{picked.length ? `${picked.length} chosen` : "Tick 2 or 3 finished runs to compare them"}</Text>
            <Button size="xs" disabled={picked.length < 2} onClick={() => setComparing(picked)} data-testid="compare-runs">Compare</Button>
          </Group>
        </Group>
        <ErrorBox error={runs.error} />
        <ScrollArea.Autosize mah={320}>
          <Table verticalSpacing={4} fz="xs" striped>
            <Table.Thead><Table.Tr>
              <Table.Th /><Table.Th>Run</Table.Th><Table.Th>Settings</Table.Th><Table.Th>Result</Table.Th><Table.Th>Time</Table.Th><Table.Th />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {list.map((r) => {
                const ok = r.status === "succeeded";
                const st = r.settings;
                return (
                  <Table.Tr key={r.job_id} bg={r.job_id === current ? "var(--mantine-color-blue-light)" : undefined}>
                    <Table.Td><Checkbox size="xs" aria-label={`compare ${r.seed ?? r.job_id}`} disabled={!ok || !r.has_bands}
                      checked={picked.includes(r.job_id)} onChange={() => toggle(r.job_id)} /></Table.Td>
                    <Table.Td>
                      <Group gap={4}><b>{r.seed}</b><Badge size="xs" color={STATUS_COLOUR[r.status] ?? "gray"}>{r.status}</Badge>
                        {r.has_dos && <Badge size="xs" variant="light">DOS</Badge>}</Group>
                      <Text size="xs" c="dimmed">{r.name}{r.created_at ? ` · ${formatDate(r.created_at)}` : ""}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{[st.protocol && st.protocol !== "-" ? st.protocol : null, st.cutoffs, st.kmesh ? `k ${st.kmesh}` : null,
                        st.spin && st.spin !== "none" ? st.spin : null, st.hubbard && st.hubbard !== "-" ? `U: ${st.hubbard}` : null]
                        .filter(Boolean).join(" · ")}</Text>
                      {st.library && st.library !== "-" && <Text size="xs" c="dimmed">{st.library}</Text>}
                    </Table.Td>
                    <Table.Td>{ok ? <Text size="xs">{r.gap_eV == null ? "" : r.gap_eV > 0 ? `gap ${r.gap_eV.toFixed(3)} eV` : "no gap"}</Text>
                      : <Text size="xs" c="red" lineClamp={2}>{r.error}</Text>}</Table.Td>
                    <Table.Td><Text size="xs">{fmtSecs(r.seconds ?? r.wall_seconds)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Button size="compact-xs" variant="light" disabled={!ok || r.job_id === current} onClick={() => onShow(r.job_id)}
                          aria-label={`show ${r.seed ?? r.job_id}`}>Show</Button>
                        <Tooltip label="A new recipe from this run's settings">
                          <Button size="compact-xs" variant="default" loading={dup.isPending && dup.variables === r.job_id}
                            onClick={() => dup.mutate(r.job_id)} aria-label={`duplicate ${r.seed ?? r.job_id}`}>Duplicate</Button>
                        </Tooltip>
                        {r.provenance_file && <Tooltip label="Provenance: recipe, pseudopotentials (md5), program, times, what to cite">
                          <Button size="compact-xs" variant="subtle" onClick={() => getProv.mutate(r)}
                            aria-label={`provenance of ${r.seed ?? r.job_id}`}>Provenance</Button></Tooltip>}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
        {comparing && <CompareView jobIds={comparing} runs={list} onClose={() => setComparing(null)} />}
      </Stack>
    </Card>
  );
}

const RUN_DASH = [undefined, "dot", "dashdot"];          // runs that lie on top of each other stay visible

/** The runs' bands in one plot (E - E_F; one colour (and dash) per run; spin down dashed) with their path's nodes. */
export function compareFigure(c: QeCompare, emin: number, emax: number) {
  const data: any[] = [];
  c.runs.forEach((r, i) => {
    const color = PALETTE[i % PALETTE.length];
    const label = r.seed ?? r.name ?? r.job_id;
    const sets: [number[][], string | undefined, string][] = r.lsda
      ? [[r.up ?? [], undefined, " ↑"], [r.down ?? [], "dash", " ↓"]] : [[r.bands ?? [], RUN_DASH[i], ""]];
    sets.forEach(([bands, dash, suffix], si) => bands.forEach((b, k) => data.push({
      x: r.x, y: b, type: "scatter", mode: "lines", line: { color, width: 1.3, dash }, name: label + suffix,
      legendgroup: `${r.job_id}${si}`, showlegend: k === 0, hovertemplate: `${label}${suffix}<br>%{y:.3f} eV<extra></extra>`,
    })));
  });
  const ticks = c.runs[0]?.ticks ?? [];
  const xmax = c.runs[0]?.x.at(-1) ?? 1;
  const layout = {
    xaxis: { range: [0, xmax], tickvals: ticks.map((t) => t[0]), ticktext: ticks.map((t) => t[1]), showgrid: false, zeroline: false },
    yaxis: { range: [emin, emax], title: { text: "E − E<sub>F</sub> (eV)" }, gridcolor: GRID, zeroline: false },
    shapes: [
      ...ticks.map((t) => ({ type: "line", xref: "x", yref: "paper", x0: t[0], x1: t[0], y0: 0, y1: 1, line: { color: "#c8c8c4", width: 1 } })),
      { type: "line", xref: "paper", yref: "y", x0: 0, x1: 1, y0: 0, y1: 0, line: { color: "#898781", dash: "dash", width: 1 } },
    ],
    legend: { orientation: "h", x: 1, xanchor: "right", y: 1.1 }, margin: { l: 60, r: 20, t: 40, b: 40 },
  };
  return { data, layout };
}

export function CompareView({ jobIds, runs, onClose }: { jobIds: string[]; runs: QeRun[]; onClose: () => void }) {
  const [range, setRange] = useState({ emin: -6, emax: 6 });
  const [onlyDiff, setOnlyDiff] = useState(false);
  const q = useQuery({ queryKey: ["qe-compare", jobIds.join(","), range], queryFn: () => qeCompare(jobIds, range.emin, range.emax),
    placeholderData: (p) => p });
  const fig = useMemo(() => (q.data ? compareFigure(q.data, range.emin, range.emax) : null), [q.data, range]);
  const c = q.data;
  const rows = (c?.rows ?? []).filter((r) => !onlyDiff || r.differs || r.result);
  const head = (id: string, i: number) => {
    const r = runs.find((x) => x.job_id === id);
    return <Text size="xs" fw={600} c={PALETTE[i % PALETTE.length]}>{r?.seed ?? id}</Text>;
  };
  return (
    <Stack gap="xs" data-testid="compare-view">
      <Group justify="space-between">
        <Text fw={600} size="sm">Comparing {jobIds.length} runs</Text>
        <Group gap="xs" align="flex-end">
          <NumberInput size="xs" w={90} label="E min (eV)" value={range.emin} step={1} max={0}
            onChange={(v) => typeof v === "number" && setRange((r) => ({ ...r, emin: v }))} />
          <NumberInput size="xs" w={90} label="E max (eV)" value={range.emax} step={1} min={0}
            onChange={(v) => typeof v === "number" && setRange((r) => ({ ...r, emax: v }))} />
          <Button size="xs" variant="default" onClick={onClose}>Close</Button>
        </Group>
      </Group>
      <ErrorBox error={q.error} />
      {c?.note && <Alert color="yellow" p="xs"><Text size="xs">{c.note}</Text></Alert>}
      {fig && <Plot data={fig.data} layout={fig.layout} height={440} />}
      {c && <>
        <Switch size="xs" label="Only the settings that differ" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.currentTarget.checked)} />
        <Table withTableBorder verticalSpacing={3} fz="xs" data-testid="compare-table">
          <Table.Thead><Table.Tr><Table.Th>Setting</Table.Th>{jobIds.map((id, i) => <Table.Th key={id}>{head(id, i)}</Table.Th>)}</Table.Tr></Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.key} bg={r.differs ? "var(--mantine-color-yellow-light)" : undefined} data-differs={r.differs || undefined}>
                <Table.Td fw={r.result ? 600 : undefined}>{r.label}</Table.Td>
                {r.values.map((v, i) => <Table.Td key={i}>{v}</Table.Td>)}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </>}
    </Stack>
  );
}
